<template>
  <v-layout wrap class="scroll-y">

    <!-- Numero prenotazioni annuale -->
    <v-flex md6 xs12 class="mb-3">
      <chart-card
        :title="$t('statistiche.label.numero_prenotazioni.title')"
        :hint="$t('statistiche.label.numero_prenotazioni.info')"
        :loading="loading"
        :data="numero_prenotazioni">
        <g-chart
          slot-scope="{ data }"
          :data="data"
          :settings="{packages: ['bar']}"
          :createChart="(el, google) => new google.charts.Bar(el)"
          :options="getOptions({legend: {position: 'top'}})"/>
      </chart-card>
    </v-flex>

    <!-- Attività tecnici mensile -->
    <v-flex md6 xs12 class="mb-3">
      <chart-card
        :title="$t('statistiche.label.attivita_tecnici.title')"
        :hint="$t('statistiche.label.attivita_tecnici.info')"
        :loading="loading"
        :data="attivita_tecnici">
        <g-chart
          type="PieChart"
          slot-scope="{ data }"
          :data="data"
          :options="getOptions()"/>
      </chart-card>
    </v-flex>

    <!-- Fatturato tarature -->
    <v-flex md6 xs12 class="mb-3">
      <chart-card
        :title="$t('statistiche.label.fatturato_tarature.title')"
        :hint="$t('statistiche.label.fatturato_tarature.info')"
        :loading="loading"
        :data="fatturato.tarature">
        <g-chart
          slot-scope="{ data }"
          :data="data"
          :settings="{packages: ['line']}"
          :createChart="(el, google) => new google.charts.Line(el)"
          :options="getOptions({legend: {position: 'top'}})"/>
      </chart-card>
    </v-flex>

    <!-- Fatturato ricambi -->
    <v-flex md6 xs12 class="mb-3">
      <chart-card
        :title="$t('statistiche.label.fatturato_ricambi.title')"
        :hint="$t('statistiche.label.fatturato_ricambi.info')"
        :loading="loading"
        :data="fatturato.ricambi">
        <g-chart
          slot-scope="{ data }"
          :data="data"
          :settings="{packages: ['line']}"
          :createChart="(el, google) => new google.charts.Line(el)"
          :options="getOptions({legend: {position: 'top'}})"/>
      </chart-card>
    </v-flex>

    <!-- Utilizzo unità mobili -->
    <v-flex md6 xs12 class="mb-3">
      <chart-card
        :title="$t('statistiche.label.attivita_unita_mobili.title')"
        :hint="$t('statistiche.label.attivita_unita_mobili.info')"
        :loading="loading"
        :data="attivita_unita_mobili">
        <g-chart
          type="PieChart"
          slot-scope="{ data }"
          :data="data"
          :options="getOptions()"/>
      </chart-card>
    </v-flex>

    <!-- Numero report per convenzione -->
    <v-flex md6 xs12 class="mb-3">
      <chart-card
        :title="$t('statistiche.label.attivita_convenzioni.title')"
        :hint="$t('statistiche.label.attivita_convenzioni.info')"
        :loading="loading"
        :data="attivita_convenzioni">
        <g-chart
          type="PieChart"
          slot-scope="{ data }"
          :data="data"
          :options="getOptions()"/>
      </chart-card>
    </v-flex>

    <!-- Numero report per regione-->
    <v-flex xs12>
      <chart-card
        :title="$t('statistiche.label.report_per_regione.title')"
        :hint="$t('statistiche.label.report_per_regione.info')"
        :loading="loading"
        :data="report_per_regione">
        <g-chart
          type="GeoChart"
          class="g-chart--geochart"
          slot-scope="{ data }"
          :data="data"
          :settings="{mapsApiKey, packages: ['geochart']}"
          :options="{region: 'IT',colorAxis: {colors: ['#f5f5f5', colors[0]]},backgroundColor: '#ffffff',datalessRegionColor: '#e6e6e6',defaultColor: '#f5f5f5',resolution:'provinces',sliceVisibilityThreshold: 1}"/>
      </chart-card>
    </v-flex>

  </v-layout>
</template>

<script type="text/jsx">
import ChartCard from '@/components/ChartCard'

export default {
    name: 'Statistiche',
    components: { ChartCard },
    data: () => ({
        mapsApiKey: process.env.VUE_APP_MAPS_KEY,
        loading: false,
        stats: null
    }),
    created () {
        this.loading = true
        this.$api.statistiche.get().then(response => {
            this.stats = response.data
            this.loading = false
        }).catch(e => {
            this.$snackbar.showServerError(e)
            this.loading = false
        })
    },
    methods: {
        getOptions (options = {}) {
            return {
                ...options,
                colors: this.colors,
                sliceVisibilityThreshold: 0,
                animation: {
                    duration: 500,
                    easing: 'out'
                }
            }
        }
    },
    computed: {
        colors () {
            let colors = Object.values(this.$vuetify.theme)
            colors.splice(1, 1)
            return colors
        },
        numero_prenotazioni () {
            if (!this.stats || this.stats.numero_prenotazioni.length === 0) return null
            let data = []
            for (let item of this.stats.numero_prenotazioni) {
                if (!data[item.year]) {
                    data[item.year] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
                }
                data[item.year][item.month - 1] = Number(item.totale)
            }
            // Header con anni ordinati e mesi nella lingua corrente
            let result = [[this.$t('label.mese'), ...Object.keys(data).sort()], ...this.$utils.date.getMonths().map(month => [month])]

            for (let i = 1; i < result[0].length; i++) {
                let year = result[0][i]
                for (let j = 0; j < 12; j++) {
                    result[j + 1].push(data[year][j])
                }
            }
            return result
        },
        attivita_tecnici () {
            if (!this.stats || !this.stats.attivita_tecnici.some(item => Number(item.totale) > 0)) return null
            return [['Task', this.$t('statistiche.label.report_compilati')]].concat(this.stats.attivita_tecnici.map(item => [`${item.nome} ${item.cognome}`, Number(item.totale)]))
        },
        fatturato () {
            if (!this.stats || this.stats.fatturato.length === 0) return { tarature: null, ricambi: null }
            let data = []
            for (let item of this.stats.fatturato) {
                if (!data[item.year]) {
                    data[item.year] = []
                    for (let i = 0; i < 12; i++) {
                        data[item.year].push({
                            fatturato: 0,
                            fatturato_ricambi: 0
                        })
                    }
                }
                data[item.year][item.month - 1].fatturato = Number(item.fatturato)
                data[item.year][item.month - 1].fatturato_ricambi = Number(item.fatturato_ricambi)
            }
            // Header con anni ordinati e mesi nella lingua corrente
            let fattTarature = [[this.$t('label.mese'), ...Object.keys(data).sort()], ...this.$utils.date.getMonths().map(month => [month])]
            let fattRicambi = [[this.$t('label.mese'), ...Object.keys(data).sort()], ...this.$utils.date.getMonths().map(month => [month])]

            for (let i = 1; i < fattTarature[0].length; i++) {
                let year = fattTarature[0][i]
                for (let j = 0; j < 12; j++) {
                    fattTarature[j + 1].push(data[year][j].fatturato)
                    fattRicambi[j + 1].push(data[year][j].fatturato_ricambi)
                }
            }
            return {
                tarature: fattTarature,
                ricambi: fattRicambi
            }
        },
        attivita_unita_mobili () {
            if (!this.stats || !this.stats.attivita_unita_mobili.some(item => Number(item.totale) > 0)) return null
            return [['Task', this.$t('statistiche.label.report_compilati')]].concat(this.stats.attivita_unita_mobili.map(item => [item.targa, Number(item.totale)]))
        },
        attivita_convenzioni () {
            if (!this.stats || !this.stats.attivita_convenzioni.some(item => Number(item.totale) > 0)) return null
            return [['Task', this.$t('statistiche.label.report_compilati')]].concat(this.stats.attivita_convenzioni.map(item => [item.nome, Number(item.totale)]))
        },
        report_per_regione () {
            if (!this.stats || !this.stats.report_per_regione.some(item => Number(item.totale) > 0)) return null
            // Mapping for standardized region names
            const regionNameMapping = {
                "Valle d'Aosta/Vallée d'Aoste": "Valle d'Aosta",
                "Trentino-Alto Adige/Südtirol": "Trentino-Alto Adige",
                // Add other mappings if needed
            };

            return [['Region', this.$t('statistiche.label.report_compilati')]].concat(this.stats.report_per_regione.map(item => [regionNameMapping[item.nome] || item.nome, Number(item.totale)]));        
        }
    }
}
</script>
